import { routes } from "@/seo/routes";
import { langOf, pathInLanguage } from "@/seo/locale";

/**
 * Les pages du site, désignées par ce qu'elles sont et non par leur URL.
 *
 * L'application vit sur des URL sans langue — /new et /game/<id> sont hors
 * index et un lien de partie doit s'ouvrir chez tout le monde — mais son
 * interface, elle, a une langue. Ses liens sortants doivent donc pointer vers
 * la bonne version de chaque page, ce que `href` résout.
 *
 * Tant qu'une page anglaise n'existe pas, le lien retombe sur la version
 * française : une interface anglaise qui renvoie vers une page française est
 * imparfaite, un lien vers une 404 est cassé. Le repli disparaît de lui-même
 * quand la traduction est déclarée, puisque c'est seoConfig qui en décide.
 */


/** Le chemin français de chaque page citée par l'application. */
export const PAGES = {
    home: "/fr",
    newGame: "/new",
    rules: "/fr/regles",
    rules501: "/fr/regle-501",
    rules301: "/fr/regle-301",
    rulesCricket: "/fr/regle-cricket",
    checkout: "/fr/table-checkouts",
    multiplayer: "/fr/multijoueur",
    stats: "/fr/stats-darts",
    training: "/fr/entrainement-darts",
    boardSetup: "/fr/distance-hauteur-cible-flechettes",
    tournament: "/fr/organiser-tournoi-flechettes",
    appScore: "/fr/app-score-flechette",
    faq: "/fr/faq",
    blog: "/fr/blog",
    legalNotice: "/fr/mentions-legales",
    privacy: "/fr/confidentialite",
    terms: "/fr/cgu",
};

/**
 * Le chemin d'une page dans la langue demandée.
 *
 * `newGame` n'a qu'une seule URL, sans langue, et la traverse donc telle
 * quelle : c'est une page de l'application, pas une page de contenu.
 */
/**
 * `PAGES[page]` en acces nu laissait passer les clefs de Object.prototype :
 * href("constructor") rendait la fonction Object au lieu de lever.
 */
const has = (page) => Object.prototype.hasOwnProperty.call(PAGES, page);

export function href(page, lang = "fr") {
    if (!has(page)) throw new Error(`page inconnue : ${page}`);
    const frPath = PAGES[page];
    if (lang === "fr") return frPath;

    return pathInLanguage(routes[frPath]?.group, lang) || frPath;
}

/**
 * Vrai si un lien vers cette page, pose depuis une page en `lang`, y reste.
 *
 * Ce n'est pas la meme question que « cette page est-elle traduite ? », et les
 * confondre coute cher : /new n'a qu'une seule URL, sans langue — un lien de
 * partie doit s'ouvrir chez tout le monde — mais son interface suit la langue
 * d'origine, donc le lecteur y reste. Le declarer « non traduit » ferait
 * disparaitre l'appel a l'action principal du site de tout pied de page
 * anglais ou allemand qui filtrerait sur cette reponse.
 *
 * `href` retombe volontairement sur le francais quand la traduction manque :
 * la question n'a donc pas de reponse lisible sur son resultat seul, et c'est
 * pourquoi elle vit ici plutot que chez l'appelant.
 */
export function staysInLanguage(page, lang) {
    if (!has(page)) throw new Error(`page inconnue : ${page}`);
    if (lang === "fr") return true;
    if (PAGES[page] === PAGES.newGame) return true;
    return langOf(href(page, lang)) === lang;
}

/** Les pages qui n'ont pas encore de version dans cette langue. */
export function missingTranslations(lang) {
    return Object.keys(PAGES).filter((page) => {
        const frPath = PAGES[page];
        // Les URL sans langue ne sont pas des traductions manquantes.
        if (frPath === PAGES.newGame) return false;
        return href(page, lang) === frPath && lang !== "fr";
    });
}
